/**
 * The Engine Room's reading of the ship, in one shape.
 *
 * One page answers seven questions: what is online, how hard each machine is
 * working, how much more it can safely take, which AI runtimes are available
 * or exhausted, whether the ship's systems are healthy, what is running or
 * held up, and what the Machinist makes of all of it.
 *
 * Everything here is a projection over rows the desk already holds - the
 * systems monitor, the AI capacity meters, the machine capacity contract, the
 * Work Board. It creates no ledger, no scheduler, no registry and no store of
 * its own, and it never invents a figure. Where a reading is absent the answer
 * is Unknown, and Unknown is never rolled into an average. In particular there
 * is no single "engine room percentage": the parts are measured in different
 * units and adding them would be arithmetic about nothing.
 *
 * Pure: no I/O, no clock. The moment arrives as `nowMs`.
 */

import type { AiCapacityMeter } from './ai-usage-view';
import { isExternalAiStatusMeter, isLocalRuntimeMeter } from './ai-usage-view';
import type { SystemMonitorCard, SystemMonitorStatus } from './systems-monitor';
import type { WorkBoardCard, WorkBoardModel } from './work-board';
import type { RunningTask } from './data';
import { machineAcceptsWork, type EngineRoomMachine } from './engine-room-machines';

/** How the whole engine room reads at a glance. */
export type EngineRoomState = 'healthy' | 'attention' | 'constrained' | 'failing' | 'unknown';

export const ENGINE_ROOM_STATE_LABELS: Readonly<Record<EngineRoomState, string>> = {
  healthy: 'Healthy',
  attention: 'Attention',
  constrained: 'Constrained',
  failing: 'Failing',
  unknown: 'Unknown',
};

/** What one AI lane can be, without pretending a percentage exists. */
export type AiLaneAvailability = 'available' | 'limited' | 'exhausted' | 'unavailable' | 'unknown';

export type AiLaneGroup = 'bridge' | 'cloud' | 'local' | 'runtime';

export interface EngineRoomAiLane {
  readonly key: string;
  readonly title: string;
  readonly group: AiLaneGroup;
  readonly availability: AiLaneAvailability;
  /** The machine this lane runs on, when it is a machine-local one. */
  readonly nodeName: string | null;
  /** Read-only / write-capable for a bridge; null for everything else. */
  readonly capability: string | null;
  readonly meter: AiCapacityMeter;
}

export interface EngineRoomAi {
  readonly lanes: readonly EngineRoomAiLane[];
  readonly available: number;
  readonly limited: number;
  readonly exhausted: number;
  readonly unavailable: number;
  readonly unknown: number;
  /** Lanes that are exhausted or unavailable - the ones that change what can run. */
  readonly issues: number;
}

export interface EngineRoomInfrastructure {
  readonly systems: readonly SystemMonitorCard[];
  readonly failing: number;
  readonly attention: number;
  readonly unavailable: number;
  readonly unknown: number;
  /** Anything that is not Healthy and not merely unreported. */
  readonly issues: number;
}

/** Where new work could go right now, and where it could not, with the reason. */
export interface EngineRoomRoute {
  readonly nodeId: string;
  readonly nodeName: string;
  readonly eligible: boolean;
  readonly placesFree: number | null;
  readonly reason: string;
}

export interface EngineRoomWork {
  readonly running: readonly RunningTask[];
  readonly queued: readonly WorkBoardCard[];
  readonly blocked: readonly WorkBoardCard[];
  readonly review: readonly WorkBoardCard[];
  readonly doneRecently: number;
  readonly queuedCount: number;
  readonly blockedCount: number;
  /** Which computer is holding how many of the running jobs. */
  readonly distribution: readonly { readonly nodeName: string; readonly running: number }[];
  readonly routes: readonly EngineRoomRoute[];
  readonly eligibleRoutes: number;
}

export interface CaptainStatus {
  readonly state: EngineRoomState;
  readonly label: string;
  readonly machinesOnline: number;
  readonly machinesTotal: number;
  /** Worker places in use and configured, across every machine that states them. */
  readonly seatsInUse: number | null;
  readonly seatsConfigured: number | null;
  readonly queuedCount: number;
  readonly blockedCount: number;
  readonly infrastructureIssues: number;
  readonly aiCapacityIssues: number;
  /** The newest reading behind anything on this page, or null when nothing has reported. */
  readonly lastUpdatedAt: string | null;
  /** Why the state reads the way it does, in the order the rules fired. */
  readonly reasons: readonly string[];
}

export interface EngineRoomModel {
  readonly captain: CaptainStatus;
  readonly machines: readonly EngineRoomMachine[];
  readonly ai: EngineRoomAi;
  readonly infrastructure: EngineRoomInfrastructure;
  readonly work: EngineRoomWork;
  /** True when nothing on this page has any current evidence behind it at all. */
  readonly evidenceMissing: boolean;
}

export interface EngineRoomInput {
  readonly machines: readonly EngineRoomMachine[];
  readonly meters: readonly AiCapacityMeter[];
  readonly systems: readonly SystemMonitorCard[];
  readonly workBoard: WorkBoardModel;
  readonly running: readonly RunningTask[];
  readonly nowMs: number;
}

function laneFromMeter(meter: AiCapacityMeter): EngineRoomAiLane {
  if (isLocalRuntimeMeter(meter)) {
    const state = meter.localRuntime.state;
    const availability: AiLaneAvailability = state === 'available'
      ? 'available'
      : state === 'busy' || state === 'memory_pressure'
        ? 'limited'
        : state === 'offline' || state === 'unavailable'
          ? 'unavailable'
          : 'unknown';
    return {
      key: meter.key,
      title: meter.title,
      group: 'local',
      availability,
      nodeName: meter.localRuntime.nodeName,
      capability: null,
      meter,
    };
  }
  if (isExternalAiStatusMeter(meter)) {
    const connection = meter.externalAi.connectionState;
    const availability: AiLaneAvailability = connection === 'connected' || connection === 'available'
      ? 'available'
      : connection === 'unavailable'
        ? 'unavailable'
        : 'unknown';
    const capability = meter.externalAi.effectiveCapability === 'read_only'
      ? 'Read-only'
      : meter.externalAi.effectiveCapability === 'write_capable'
        ? 'Write-capable'
        : meter.externalAi.effectiveCapability === 'unavailable'
          ? 'Unavailable'
          : 'Capability Unknown';
    return {
      key: meter.key,
      title: meter.title,
      group: meter.externalAi.entityKind === 'external_mcp_bridge' ? 'bridge' : 'runtime',
      availability,
      nodeName: null,
      capability,
      meter,
    };
  }
  const availability: AiLaneAvailability = meter.quotaSignal === 'exhausted'
    ? 'exhausted'
    : meter.quotaSignal === 'signed_out'
      ? 'unavailable'
      : meter.quotaSignal === 'rate_limited' || meter.quotaSignal === 'near_limit'
        ? 'limited'
        : meter.quotaSignal === 'ok'
          ? (meter.band === 'exhausted' || meter.band === 'hard_exceeded'
            ? 'exhausted'
            : meter.band === 'warn' || meter.band === 'soft_exceeded' || meter.band === 'reserve_breached'
              ? 'limited'
              : 'available')
          : 'unknown';
  return {
    key: meter.key,
    title: meter.title,
    group: 'cloud',
    availability,
    nodeName: null,
    capability: null,
    meter,
  };
}

export function engineRoomAi(meters: readonly AiCapacityMeter[]): EngineRoomAi {
  const lanes = meters.map(laneFromMeter);
  const count = (value: AiLaneAvailability) => lanes.filter((lane) => lane.availability === value).length;
  const exhausted = count('exhausted');
  const unavailable = count('unavailable');
  return {
    lanes,
    available: count('available'),
    limited: count('limited'),
    exhausted,
    unavailable,
    unknown: count('unknown'),
    issues: exhausted + unavailable,
  };
}

function countSystems(systems: readonly SystemMonitorCard[], status: SystemMonitorStatus): number {
  return systems.filter((card) => card.status === status).length;
}

export function engineRoomInfrastructure(systems: readonly SystemMonitorCard[]): EngineRoomInfrastructure {
  const failing = countSystems(systems, 'Failing');
  const attention = countSystems(systems, 'Attention');
  const unavailable = countSystems(systems, 'Unavailable');
  return {
    systems,
    failing,
    attention,
    unavailable,
    unknown: countSystems(systems, 'Unknown'),
    issues: failing + attention + unavailable,
  };
}

function cardsIn(board: WorkBoardModel, id: WorkBoardCard['column']): readonly WorkBoardCard[] {
  return board.columns.find((column) => column.id === id)?.cards ?? [];
}

/**
 * Where a new job could be routed at this moment.
 *
 * This states eligibility; it does not route anything. A machine that is not
 * eligible carries the reason the capacity contract already gives, so the
 * owner reads the same words here as on the machine's own card.
 */
export function engineRoomRoutes(machines: readonly EngineRoomMachine[]): readonly EngineRoomRoute[] {
  return machines.map((machine) => {
    const eligible = machineAcceptsWork(machine);
    const reason = eligible
      ? machine.placesFree === 1 ? '1 place free' : `${machine.placesFree} places free`
      : machine.connection !== 'connected'
        ? machine.connection === 'stale' ? 'Reading is stale; the machine is out of touch' : 'Not connected'
        : machine.readinessLine;
    return {
      nodeId: machine.nodeId,
      nodeName: machine.name,
      eligible,
      placesFree: machine.placesFree,
      reason,
    };
  });
}

export function engineRoomWork(input: {
  readonly workBoard: WorkBoardModel;
  readonly running: readonly RunningTask[];
  readonly machines: readonly EngineRoomMachine[];
}): EngineRoomWork {
  const queued = [...cardsIn(input.workBoard, 'ready'), ...cardsIn(input.workBoard, 'planned')];
  const blocked = cardsIn(input.workBoard, 'blocked');
  const review = cardsIn(input.workBoard, 'review');
  const distribution = input.machines.map((machine) => ({
    nodeName: machine.name,
    running: input.running.filter((row) => row.node_name === machine.name).length,
  }));
  const routes = engineRoomRoutes(input.machines);
  return {
    running: input.running,
    queued,
    blocked,
    review,
    doneRecently: cardsIn(input.workBoard, 'done').length,
    queuedCount: queued.length,
    blockedCount: blocked.length,
    distribution,
    routes,
    eligibleRoutes: routes.filter((route) => route.eligible).length,
  };
}

/** The newest timestamp anything on this page was actually measured at. */
function newestReading(input: EngineRoomInput): string | null {
  const stamps: string[] = [];
  for (const machine of input.machines) {
    if (machine.observedAt !== null) stamps.push(machine.observedAt);
    if (machine.lastSeenAt !== null) stamps.push(machine.lastSeenAt);
  }
  for (const card of input.systems) if (card.observedAt !== null) stamps.push(card.observedAt);
  const parsed = stamps.map((value) => Date.parse(value)).filter((value) => !Number.isNaN(value));
  if (parsed.length === 0) return null;
  return new Date(Math.max(...parsed)).toISOString();
}

/**
 * Worker places in use and configured, across the machines that state them.
 *
 * A machine that does not state its places is left out of both totals rather
 * than counted as nought, so "3 of 6" never quietly means "3 of the ones that
 * answered". When no machine states places at all, both are Unknown.
 */
function seats(machines: readonly EngineRoomMachine[]): { inUse: number | null; configured: number | null } {
  const stated = machines.filter((machine) => machine.seats !== null);
  if (stated.length === 0) return { inUse: null, configured: null };
  const configured = stated.reduce((total, machine) => total + (machine.seats ?? 0), 0);
  const withRuns = stated.filter((machine) => machine.activeRuns !== null);
  const inUse = withRuns.length === 0
    ? null
    : withRuns.reduce((total, machine) => total + (machine.activeRuns ?? 0), 0);
  return { inUse, configured };
}

/**
 * How the engine room reads, and why.
 *
 * The rules run in order of seriousness and each one states its own reason, so
 * the top line is never a mood: it is the first rule that matched, and the
 * owner can read the rest underneath it.
 */
export function captainStatus(input: EngineRoomInput & {
  readonly ai: EngineRoomAi;
  readonly infrastructure: EngineRoomInfrastructure;
  readonly work: EngineRoomWork;
}): CaptainStatus {
  const machines = input.machines;
  const online = machines.filter((machine) => machine.connection === 'connected');
  const place = seats(machines);
  const reasons: string[] = [];

  const noEvidence = machines.length === 0
    && input.infrastructure.issues === 0
    && input.infrastructure.systems.every((card) => card.status === 'Unknown')
    && input.ai.lanes.length === 0;

  if (input.infrastructure.failing > 0) {
    reasons.push(`${input.infrastructure.failing} ship system${input.infrastructure.failing === 1 ? '' : 's'} reporting a failure`);
  }
  if (machines.length > 0 && online.length === 0) {
    reasons.push('No computer is currently in touch');
  }
  if (machines.length > 0 && online.length > 0 && input.work.eligibleRoutes === 0) {
    reasons.push('No connected computer can take new work right now');
  }
  if (input.ai.lanes.length > 0 && input.ai.available === 0 && input.ai.limited === 0 && input.ai.unknown < input.ai.lanes.length) {
    reasons.push('No AI lane is reporting available capacity');
  }
  if (input.ai.exhausted > 0) reasons.push(`${input.ai.exhausted} AI lane${input.ai.exhausted === 1 ? '' : 's'} exhausted`);
  if (input.ai.unavailable > 0) reasons.push(`${input.ai.unavailable} AI lane${input.ai.unavailable === 1 ? '' : 's'} unavailable`);
  if (input.infrastructure.attention > 0) reasons.push(`${input.infrastructure.attention} ship system${input.infrastructure.attention === 1 ? '' : 's'} needing attention`);
  if (input.infrastructure.unavailable > 0) reasons.push(`${input.infrastructure.unavailable} ship system${input.infrastructure.unavailable === 1 ? '' : 's'} unavailable`);
  const heldBack = machines.filter((machine) => machine.readiness === 'held_back');
  if (heldBack.length > 0) reasons.push(`${heldBack.length} computer${heldBack.length === 1 ? '' : 's'} held back by a reading`);
  const atLimit = machines.filter((machine) => machine.readiness === 'at_limit');
  if (atLimit.length > 0) reasons.push(`${atLimit.length} computer${atLimit.length === 1 ? '' : 's'} at its limit`);
  const stale = machines.filter((machine) => machine.stale);
  if (stale.length > 0) reasons.push(`${stale.length} machine reading${stale.length === 1 ? '' : 's'} older than five minutes`);
  const outOfTouch = machines.filter((machine) => machine.connection !== 'connected');
  if (outOfTouch.length > 0) reasons.push(`${outOfTouch.length} computer${outOfTouch.length === 1 ? '' : 's'} out of touch`);
  if (input.work.blockedCount > 0) reasons.push(`${input.work.blockedCount} piece${input.work.blockedCount === 1 ? '' : 's'} of work blocked`);

  const state: EngineRoomState = noEvidence
    ? 'unknown'
    : input.infrastructure.failing > 0 || (machines.length > 0 && online.length === 0)
      ? 'failing'
      : (machines.length > 0 && input.work.eligibleRoutes === 0)
        || (input.ai.lanes.length > 0 && input.ai.available === 0 && input.ai.limited === 0 && input.ai.unknown < input.ai.lanes.length)
        ? 'constrained'
        : reasons.length > 0
          ? 'attention'
          : 'healthy';

  return {
    state,
    label: ENGINE_ROOM_STATE_LABELS[state],
    machinesOnline: online.length,
    machinesTotal: machines.length,
    seatsInUse: place.inUse,
    seatsConfigured: place.configured,
    queuedCount: input.work.queuedCount,
    blockedCount: input.work.blockedCount,
    infrastructureIssues: input.infrastructure.issues,
    aiCapacityIssues: input.ai.issues,
    lastUpdatedAt: newestReading(input),
    reasons,
  };
}

export function buildEngineRoom(input: EngineRoomInput): EngineRoomModel {
  const ai = engineRoomAi(input.meters);
  const infrastructure = engineRoomInfrastructure(input.systems);
  const work = engineRoomWork({
    workBoard: input.workBoard, running: input.running, machines: input.machines,
  });
  const captain = captainStatus({ ...input, ai, infrastructure, work });
  return {
    captain,
    machines: input.machines,
    ai,
    infrastructure,
    work,
    evidenceMissing: captain.state === 'unknown',
  };
}
